import { Injectable } from '@angular/core';
import { Apollo, SubscriptionResult } from 'apollo-angular';
import { TimelineSubscription } from '../../../shared/data/gql/subscription/timeline.subscription';
import { GetPostCommentsQueryInput } from '../../../shared/data/inputs/get-post-comments-query.input';
import { ApolloQueryResult } from 'apollo-client';
import { PostCommentsResponse } from '../../../shared/data/responses/post-comments.response';
import { Observable } from 'rxjs';
import { PostCommentQuery } from '../../../shared/data/gql/query/post-comment.query';
import { PostQueryInput } from '../../../shared/data/inputs/post-query.input';
import { TimelineQueryResponse } from '../../../shared/data/responses/timeline-query.response';
import { TimelineQuery } from '../../../shared/data/gql/query/timeline.query';
import { GetSinglePostResponse } from '../../../shared/data/responses/get-single-post.response';
import { getSinglePostQuery } from '../../../shared/data/gql/query/get-single-post.query';
import { PostWithCommentsResponse } from '../../../shared/data/responses/post-with-comments.response';
import { getPostWithCommentsQuery } from '../../../shared/data/gql/query/get-post-with-comments.query';
import { NewPostMutationInput } from '../../../shared/data/inputs/new-post-mutation.input';
import { CreatePostMutationResponse } from '../../../shared/data/responses/create-post-mutation.response';
import { FetchResult } from 'apollo-link';
import { NewPostQuery } from '../../../shared/data/gql/query/new-post.query';
import { CreateCommentMutationInput } from '../../../shared/data/inputs/create-comment-mutation.input';
import { CreateCommentMutationResponse } from '../../../shared/data/responses/create-comment-mutation-response';
import { NewCommentMutation } from '../../../shared/data/gql/mutation/new-comment.mutation';
import { VotePostInput } from '../../../shared/data/inputs/vote-post.input';
import { UpVotePostResponse } from '../../../shared/data/responses/up-vote-post.response';
import { UpVotePostMutation } from '../../../shared/data/gql/mutation/up-vote-post.mutation';
import { DownVotePostResponse } from '../../../shared/data/responses/down-vote-post.response';
import { DownVotePostMutation } from '../../../shared/data/gql/mutation/down-vote-post.mutation';
import { UnVotePostResponse } from '../../../shared/data/responses/un-vote-post.response';
import { unVotePostMutation } from '../../../shared/data/gql/mutation/un-vote-post.mutation';
import { PostInput } from '../../../shared/data/inputs/post.input';
import { FavoritePostResponse } from '../../../shared/data/responses/favorite-post.response';
import { favoritePostMutation } from '../../../shared/data/gql/mutation/favorite-post.mutation';
import { UnFavoritePostResponse } from '../../../shared/data/responses/un-favorite-post.response';
import { unFavoritePostMutation } from '../../../shared/data/gql/mutation/un-favorite-post.mutation';
import { VoteCommentInput } from '../../../shared/data/inputs/vote-comment.input';
import { UpVoteCommentMutationResponse } from '../../../shared/data/responses/up-vote-comment-mutation.response';
import { UpVoteCommentMutation } from '../../../shared/data/gql/mutation/up-vote-comment.mutation';
import { DownVoteCommentMutationResponse } from '../../../shared/data/responses/down-vote-comment-mutation.response';
import { DownVoteCommentMutation } from '../../../shared/data/gql/mutation/down-vote-comment.mutation';
import { UnVoteCommentResponse } from '../../../shared/data/responses/un-vote-comment.response';
import { unVoteCommentMutation } from '../../../shared/data/gql/mutation/un-vote-comment.mutation';
import { TimelineSubscriptionInput } from '../../../shared/data/inputs/timeline-subscription.input';
import { TimelineSubscriptionResponse } from '../../../shared/data/responses/timeline-subscription.response';
import { PostDeleteResponse } from '../../../shared/data/responses/post-delete.response';
import { DeletePostMutation } from '../../../shared/data/gql/mutation/delete-post.mutation';
import { CommentInput } from '../../../shared/data/inputs/comment.input';
import { DeleteCommentResponse } from '../../../shared/data/responses/delete-comment.response';
import { DeleteCommentMutation } from '../../../shared/data/gql/mutation/delete-comment.mutation';
import { CastPollVoteInput } from '../../../shared/data/inputs/cast-poll-vote.input';
import { CastPollVoteResponse } from '../../../shared/data/responses/cast-poll-vote.response';
import { castPollVoteMutation } from '../../../shared/data/gql/mutation/cast-poll-vote.mutation';
import { GetCommentRepliesResponse } from '../../../shared/data/responses/get-comment-replies.response';
import { getCommentRepliesQuery } from '../../../shared/data/gql/query/get-comment-replies.query';
import { GetCommentsRepliesInput } from '../../../shared/data/inputs/get-comments-replies.input';

@Injectable({
  providedIn: 'root'
})
export class PostService {
  constructor(
    private apollo: Apollo,
    private timelineSubscription: TimelineSubscription
  ) {}

  /**
   *  get comment from post
   * */
  getPostComment(
    input: GetPostCommentsQueryInput
  ): Observable<ApolloQueryResult<PostCommentsResponse>> {
    return this.apollo.query<PostCommentsResponse>({
      query: PostCommentQuery,
      variables: input
    });
  }

  getPosts(
    inputs: PostQueryInput
  ): Observable<ApolloQueryResult<TimelineQueryResponse>> {
    return this.apollo.query<TimelineQueryResponse>({
      query: TimelineQuery,
      variables: inputs
    });
  }

  getSinglePost(
    postId: string
  ): Observable<ApolloQueryResult<GetSinglePostResponse>> {
    return this.apollo.query<GetSinglePostResponse>({
      query: getSinglePostQuery,
      variables: {
        postID: postId
      }
    });
  }

  getSinglePostWithComments(
    input: GetPostCommentsQueryInput
  ): Observable<ApolloQueryResult<PostWithCommentsResponse>> {
    return this.apollo.query<PostWithCommentsResponse>({
      query: getPostWithCommentsQuery,
      variables: input
    });
  }

  postNormalPosts(
    inputs: NewPostMutationInput
  ): Observable<FetchResult<CreatePostMutationResponse>> {
    return this.apollo.mutate<CreatePostMutationResponse>({
      mutation: NewPostQuery,
      variables: {
        post: inputs
      }
    });
  }

  createComment(
    inputs: CreateCommentMutationInput
  ): Observable<FetchResult<CreateCommentMutationResponse>> {
    return this.apollo.mutate<CreateCommentMutationResponse>({
      mutation: NewCommentMutation,
      variables: inputs
    });
  }

  upVotePost(
    input: VotePostInput
  ): Observable<FetchResult<UpVotePostResponse>> {
    return this.apollo.mutate<UpVotePostResponse>({
      mutation: UpVotePostMutation,
      variables: input
    });
  }

  downVotePost(
    input: VotePostInput
  ): Observable<FetchResult<DownVotePostResponse>> {
    return this.apollo.mutate<DownVotePostResponse>({
      mutation: DownVotePostMutation,
      variables: input
    });
  }

  unVotePost(
    input: VotePostInput
  ): Observable<FetchResult<UnVotePostResponse>> {
    return this.apollo.mutate<UnVotePostResponse>({
      mutation: unVotePostMutation,
      variables: input
    });
  }

  favoritePost(
    input: PostInput
  ): Observable<FetchResult<FavoritePostResponse>> {
    return this.apollo.mutate<FavoritePostResponse>({
      mutation: favoritePostMutation,
      variables: input
    });
  }

  unFavoritePost(
    input: PostInput
  ): Observable<FetchResult<UnFavoritePostResponse>> {
    return this.apollo.mutate<UnFavoritePostResponse>({
      mutation: unFavoritePostMutation,
      variables: input
    });
  }

  upVoteComment(
    input: VoteCommentInput
  ): Observable<FetchResult<UpVoteCommentMutationResponse>> {
    return this.apollo.mutate<UpVoteCommentMutationResponse>({
      mutation: UpVoteCommentMutation,
      variables: input
    });
  }

  downVoteComment(
    input: VoteCommentInput
  ): Observable<FetchResult<DownVoteCommentMutationResponse>> {
    return this.apollo.mutate<DownVoteCommentMutationResponse>({
      mutation: DownVoteCommentMutation,
      variables: input
    });
  }

  unVoteComment(
    input: VoteCommentInput
  ): Observable<FetchResult<UnVoteCommentResponse>> {
    return this.apollo.mutate<UnVoteCommentResponse>({
      mutation: unVoteCommentMutation,
      variables: input
    });
  }

  getTimeLineSubscription(
    input: TimelineSubscriptionInput
  ): Observable<SubscriptionResult<TimelineSubscriptionResponse>> {
    return this.timelineSubscription.subscribe(input);
  }

  deletePost(input: PostInput): Observable<FetchResult<PostDeleteResponse>> {
    return this.apollo.mutate<PostDeleteResponse>({
      variables: input,
      mutation: DeletePostMutation
    });
  }

  deleteComment(
    input: CommentInput
  ): Observable<FetchResult<DeleteCommentResponse>> {
    return this.apollo.mutate<DeleteCommentResponse>({
      mutation: DeleteCommentMutation,
      variables: input
    });
  }

  castPostPoll(
    input: CastPollVoteInput
  ): Observable<FetchResult<CastPollVoteResponse>> {
    return this.apollo.mutate<CastPollVoteResponse>({
      mutation: castPollVoteMutation,
      variables: input
    });
  }

  getCommentReply(
    input: GetCommentsRepliesInput
  ): Observable<ApolloQueryResult<GetCommentRepliesResponse>> {
    return this.apollo.query<GetCommentRepliesResponse>({
      query: getCommentRepliesQuery,
      variables: input
    });
  }
}
